import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ButtonModel, ModalContext, Text } from '@notino/react-styleguide';
import { CatalogVariantFragmentFragment } from '@notino/shared/definitions/types';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { useFormatPrice } from '@components/PriceLabel/useFormattedPrice';
import { isDesktop } from '@utils/helpers';

import { messages } from './messages';
import {
  LineThrough,
  ListItems,
  ModalContent,
  MoreInfoLink,
  OkButton,
} from './styled';
import { displayPercentageDiscount } from './utils';

type Props = {
  percentage: number;
  percentageFromMinimal: number | undefined;
  discountedPrice: number;
  legalText: React.ReactNode;
  variant: CatalogVariantFragmentFragment;
};

export const InfoModalContent = ({
  percentage,
  percentageFromMinimal,
  discountedPrice,
  legalText,
  variant,
}: Props) => {
  const minPrice = variant.recentMinPrice;
  const formatPrice = useFormatPrice();
  const { formatMessage } = useIntl();
  const { hideModal } = ModalContext.useModalContext();
  const { hideDrawer } = useDrawer();

  return (
    <>
      {isDesktop() && (
        <Text typography="titleLarge">
          <FormattedMessage {...messages.modalTitle} />
        </Text>
      )}

      <ModalContent>
        <ListItems>
          <Text typography="bodyRegular" as="p">
            <span>1.</span>
            <div>
              <p>
                <FormattedMessage {...messages.modalPriceAfterDiscountText} />
              </p>
              <Text
                block={true}
                typography="labelRegular400"
                color="text.tertiary"
              >
                *{legalText}
              </Text>
              <Text
                style={{ marginTop: 8 }}
                typography="titleMedium"
                color="text.highlight"
              >
                {
                  formatPrice({ value: discountedPrice })
                    .formattedPriceWithCurrency
                }
              </Text>
            </div>
          </Text>

          <Text typography="bodyRegular" as="p">
            <span>2.</span>
            <div>
              <p>
                <FormattedMessage {...messages.modalCurrentPriceText} />
              </p>
              <Text
                style={{ marginTop: 4 }}
                typography="labelRegular400"
                color="text.secondary"
              >
                <LineThrough>
                  {formatPrice(variant.price).formattedPriceWithCurrency}
                </LineThrough>
              </Text>
              <Text
                typography="labelRegular400"
                color="text.highlight"
                style={{ marginLeft: 6 }}
              >
                {displayPercentageDiscount(percentage)}
              </Text>
            </div>
          </Text>

          {minPrice && (
            <Text typography="bodyRegular" as="p">
              <span>3.</span>
              <div>
                <p>
                  <FormattedMessage {...messages.modalRecentMinPriceText} />
                </p>
                <Text
                  style={{ marginTop: 4 }}
                  typography="labelRegular400"
                  color="text.secondary"
                >
                  <LineThrough>
                    {formatPrice(minPrice).formattedPriceWithCurrency}
                  </LineThrough>
                </Text>
                <Text
                  typography="labelRegular400"
                  color="text.highlight"
                  style={{ marginLeft: 6 }}
                >
                  {displayPercentageDiscount(percentageFromMinimal)}
                </Text>
              </div>
            </Text>
          )}
        </ListItems>

        <MoreInfoLink
          target="_blank"
          href={formatMessage(messages.modalMoreInfoLinkUrl)}
        >
          <FormattedMessage {...messages.modalMoreInfoLinkText} />
        </MoreInfoLink>

        <OkButton
          onClick={() => (isDesktop() ? hideModal() : hideDrawer())}
          buttonStyle={ButtonModel.Styles.primary}
        >
          <FormattedMessage {...messages.modalOkButtonText} />
        </OkButton>
      </ModalContent>
    </>
  );
};
